import React, { useCallback, useEffect, useState } from "react";
import { History, Loader2, RefreshCw, ShieldCheck, Users, Coins, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { timeAgo } from "@/lib/timeAgo";

// Lot 708 — Admin › Configuration › « Droits acquis » : chaque enregistrement d'une règle surveillée (Privilèges, Monétisation)
// archive l'ancienne version avec sa date. Un membre inscrit AVANT un changement garde l'ancienne règle si elle lui est plus
// favorable ; si la nouvelle est plus avantageuse, tout le monde en profite. Par changement : protéger les anciens / appliquer à tous.
const PROFILE = { standard_unverified: "Standard · sans badge", standard_verified: "Standard · avec badge", preacher_unverified: "Prédicateur · sans badge",
  preacher_verified: "Prédicateur · avec badge", artist_unverified: "Artiste · sans badge", artist_verified: "Artiste · avec badge",
  influencer_unverified: "Influenceur · sans badge", influencer_verified: "Influenceur · avec badge" };
const FIELD = { can_go_live: "Peut passer en direct", min_followers_live: "Abonnés mini pour le live", max_live_duration_min: "Durée max d'un live (min)",
  max_followers: "Abonnés max", can_receive_offerings: "Offrandes / dons", can_post_videos: "Publier des vidéos", can_create_ads: "Créer des publicités",
  max_stories_per_day: "Stories / 24 h" };
const RULE = { badge: "Règle « badge »", identity: "Règle « identité »", badge_identity_role: "Règle « badge + identité + rôle »" };
const GRANT = { live_extended: "LIVE étendu", followers_extended: "Abonnés étendus", offerings: "Offrandes", withdraw: "Retraits", premium: "Premium" };
const REFERRAL = { points_per_invite: "Points par invité validé", badge_year_points: "Yalola Vérifié 1 an (pts)", point_value: "Valeur d'1 point",
  min_convert_points: "Conversion minimale (pts)", listing_boost_points: "Annonce en avant — prix (pts)", listing_boost_days: "Annonce en avant — durée (jours)",
  live_boost_points: "Boost de live — prix (pts)", live_boost_hours: "Boost de live — durée (heures)" };
const SCOPE = { privileges: ["Privilèges", Users, "#1D4ED8", "#DBEAFE"], referral: ["Monétisation", Coins, "#92400E", "#FEF3C7"] };
const UNLIMITED = new Set(["max_live_duration_min", "max_followers", "max_stories_per_day"]);

export const labelFor = (scope, path) => {
  if (scope === "referral") return REFERRAL[path] || path;
  const [root, a, b] = path.split(".");
  if (root === "profiles") return `${PROFILE[a] || a} — ${FIELD[b] || b}`;
  if (root === "rules") return RULE[a] || path;
  return path;
};

export const formatValue = (path, v) => {
  if (Array.isArray(v)) return v.length ? v.map((g) => GRANT[g] || g).join(", ") : "aucun avantage";
  if (typeof v === "boolean") return v ? "Oui" : "Non";
  if (v === null || v === undefined) return "—";
  const field = path.split(".").pop();
  if (UNLIMITED.has(field) && Number(v) === 0) return "∞ (illimité)";
  if (field === "badge_year_points" && Number(v) === 0) return "0 (non achetable)";
  return String(v);
};

const VersionCard = ({ v, onProtect, busy }) => {
  const [label, Icon, color, bg] = SCOPE[v.scope] || [v.scope, Info, "#374151", "#F3F4F6"];
  const protect = v.protect !== false;
  return (
    <div className="yalola-card p-4 space-y-2" data-testid={`acquired-version-${v.id}`} data-protect={protect ? "1" : "0"}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="chip text-[11px] flex items-center gap-1" style={{ background: bg, color }}><Icon size={11} /> {label}</span>
        <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }} title={v.changed_at}>
          {new Date(v.changed_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })} · {timeAgo(v.changed_at)}
          {v.changed_by_name ? ` · par ${v.changed_by_name}` : ""}
        </span>
        <label className="ml-auto flex items-center gap-2 text-xs cursor-pointer" data-testid={`acquired-protect-row-${v.id}`}>
          <Switch checked={protect} disabled={busy} onCheckedChange={(val) => onProtect(v.id, val)} data-testid={`acquired-protect-${v.id}`} />
          <span className="font-semibold" style={{ color: protect ? "#1B7A3D" : "#B42318" }}>{protect ? "Anciens membres protégés" : "Appliqué à tous"}</span>
        </label>
      </div>
      <ul className="text-sm space-y-1">
        {(v.changes || []).map((c) => (
          <li key={c.path} className="flex items-baseline gap-2 flex-wrap" data-testid={`acquired-change-${v.id}-${c.path}`}>
            <span className="font-semibold">{labelFor(v.scope, c.path)}</span>
            <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
              <s>{formatValue(c.path, c.old)}</s> → <b style={{ color: "var(--yalola-text)" }}>{formatValue(c.path, c.new)}</b>
            </span>
          </li>
        ))}
      </ul>
      <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
        {protect ? "Les membres inscrits avant cette date gardent l'ancienne valeur quand elle leur est plus favorable."
          : "Cette version s'applique à tous les membres, y compris ceux inscrits avant la date du changement."}
      </p>
    </div>
  );
};

export const AcquiredRightsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/acquired-rights").then(({ data: d }) => setData(d)).catch(() => setData(false)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      await api.patch("/admin/acquired-rights", { enabled });
      setData((d) => ({ ...(d || {}), enabled }));
      toast.success(enabled ? "Droits acquis activés : les anciens membres gardent leurs avantages" : "Droits acquis désactivés : les règles courantes s'appliquent à tous");
    } catch (e) { toast.error(e?.response?.data?.detail || e?.friendlyMessage || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };
  const protect = async (id, value) => {
    setBusy(true);
    try {
      await api.patch(`/admin/acquired-rights/${id}`, { protect: value });
      setData((d) => ({ ...(d || {}), versions: (d?.versions || []).map((v) => (v.id === id ? { ...v, protect: value } : v)) }));
      toast.success(value ? "Anciens membres protégés pour ce changement" : "Ce changement s'applique désormais à tous");
    } catch (e) { toast.error(e?.response?.data?.detail || e?.friendlyMessage || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };

  const enabled = data ? data.enabled === true : false; // garde-fou : désactivé par défaut (règle courante pour tous)
  const versions = data?.versions || [];
  return (
    <div className="space-y-4" data-testid="acquired-rights-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-extrabold font-display">Droits acquis</h2>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Quand une règle change, les membres déjà inscrits conservent leurs avantages. Chaque enregistrement dans Paramètres › Privilèges et Monétisation est archivé ici avec sa date.</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-full ml-auto" onClick={load} disabled={loading} data-testid="acquired-refresh">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </Button>
      </div>

      <label className="yalola-card p-4 flex items-start gap-3 cursor-pointer" data-testid="acquired-enabled-row" data-enabled={enabled ? "1" : "0"}>
        <Switch checked={enabled} disabled={busy || loading} onCheckedChange={toggle} className="mt-0.5" data-testid="acquired-enabled" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold flex items-center gap-1.5"><ShieldCheck size={14} style={{ color: enabled ? "#1B7A3D" : "#B42318" }} /> Protection des anciens membres {enabled ? "activée" : "désactivée"}</div>
          <div className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>
            {enabled ? "Un membre est évalué avec la règle en vigueur à sa date d'inscription si elle lui est plus favorable (seuil d'abonnés plus bas, durée ou plafond plus hauts, prix en points plus bas, droit ouvert). Si la nouvelle règle est plus avantageuse, tout le monde en profite."
              : "GARDE-FOU (comportement historique) : les règles courantes s'appliquent à tous les membres, quelle que soit leur date d'inscription. L'historique continue d'être archivé ; un changement fait pendant que la protection est désactivée est marqué « Appliqué à tous » (tu peux le protéger après coup)."}
          </div>
        </div>
      </label>

      <div className="yalola-card p-4 text-xs space-y-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="acquired-howto">
        <div className="text-sm font-semibold flex items-center gap-1.5" style={{ color: "var(--yalola-text)" }}><History size={14} /> Comment ça marche</div>
        <p>• <b>Privilèges</b> protégés : abonnés mini pour passer en direct, durée max d'un live, abonnés max, stories / 24 h, vidéos, publicités, offrandes (les retraits exigent toujours une identité vérifiée).</p>
        <p>• <b>Monétisation</b> protégée : prix en points de Yalola Vérifié et des boosts, valeur du point, conversion minimale, points par invité, durées des boosts.</p>
        <p>• Les interrupteurs ON/OFF (système de privilèges, points, boutique…) ne sont pas des droits acquis : ils valent pour tout le monde.</p>
        <p>• Exemple : le live passe de 100 à 500 abonnés aujourd'hui → un membre inscrit hier garde 100 ; un membre inscrit demain a 500. Repasser à 50 profite à tout le monde. Mettre un prix à 0 (= non achetable) vaut pour tous.</p>
        <p>• Par changement, la bascule « Anciens membres protégés / Appliqué à tous » permet de lever ou rétablir la protection d'une modification précise.</p>
      </div>

      <div className="space-y-3" data-testid="acquired-history">
        <div className="text-sm font-semibold font-display">Historique des changements de règles ({versions.length})</div>
        {data === false && <p className="text-sm" style={{ color: "#B42318" }} data-testid="acquired-error">Chargement impossible.</p>}
        {data && versions.length === 0 && (
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="acquired-empty">Aucun changement archivé pour l'instant. Les prochains enregistrements dans Paramètres › Privilèges et Monétisation apparaîtront ici.</p>
        )}
        {versions.map((v) => <VersionCard key={v.id} v={v} onProtect={protect} busy={busy} />)}
      </div>
    </div>
  );
};

export default AcquiredRightsPanel;
